import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, formatErr } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function LoginPage() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}`);
      nav(user.role === "admin" ? "/admin" : from);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google/callback";
    const clientId = "275423587761-kegvoerkrlmnvrnbn157ckh87ieus885.apps.googleusercontent.com";
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem("g_state", state);
    sessionStorage.setItem("g_next", "/dashboard");
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      include_granted_scopes: "true",
      prompt: "select_account",
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16" data-testid="login-page">
      <div className="max-w-md w-full">
        <div className="flex justify-center mb-8">
          <BrandLogo height={64} linkTo="/" darkenGrey />
        </div>
        <div className="ser-card p-8 md:p-10">
          <p className="eyebrow mb-3">Welcome back</p>
          <h1 className="font-serif-display text-3xl mb-6" style={{ color: "var(--ser-primary-dark)" }}>
            Sign in to your account
          </h1>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="ser-label">Email</label>
              <input required type="email" className="ser-input" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="ser-label">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold hover:underline"
                  style={{ color: "var(--ser-primary)" }}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <input required type="password" className="ser-input" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="login-password" />
            </div>
            <button type="submit" disabled={loading} className="btn-ser-primary w-full" data-testid="login-submit-btn">
              <LogIn className="w-4 h-4" />
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="flex items-center my-6 gap-3">
            <div className="flex-1 h-px" style={{ backgroundColor: "var(--ser-sand-dark)" }} />
            <span className="text-xs" style={{ color: "var(--ser-text-muted)" }}>or</span>
            <div className="flex-1 h-px" style={{ backgroundColor: "var(--ser-sand-dark)" }} />
          </div>

          <button onClick={googleLogin} className="btn-ser-outline w-full" data-testid="google-login-btn">
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <p className="text-center text-sm mt-6" style={{ color: "var(--ser-text-muted)" }}>
            Don't have an account?{" "}
            <Link to="/register" className="font-semibold hover:underline" style={{ color: "var(--ser-primary)" }}
              data-testid="go-to-register">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
